import { normalizeAccents } from "../importTools.js";

/**
 * Build annotations from a GeoJSON FeatureCollection using the current view + fieldMatching.
 * Returns annotations + warning strings (same shape as existing UI expects).
 */
export function buildAnnotationsFromGeojson({
  geojson,
  view,
  fieldMatching,
  isDXFFile,
  layerMapping,
  cleanCorruptedAnnotation,
}) {
  if (!geojson || geojson.type !== "FeatureCollection" || !Array.isArray(geojson.features) || !view) {
    return { annotations: [], warnings: [] };
  }

  const annotations = [];

  // Error management
  const errors_types = {};
  const add_error = (error_type, feature_id) => {
    if (errors_types[error_type] == null) errors_types[error_type] = [];
    errors_types[error_type].push(feature_id);
  };

  let skippedCount = 0;
  const skippedReasons = {};

  // Helper function to clean coordinates (replace null/undefined/NaN with 0)
  const cleanCoordinates = (coords) => {
    if (!Array.isArray(coords)) return coords;

    const cleanPoint = (point) => {
      if (!Array.isArray(point)) return point;
      return point.map((coord) => {
        if (typeof coord !== "number" || Number.isNaN(coord) || !Number.isFinite(coord) || coord == null) {
          return 0;
        }
        return coord;
      });
    };

    // For Point: [x, y]
    if (coords.length >= 2 && typeof coords[0] === "number") {
      return cleanPoint(coords);
    }
    // For LineString / internal polygon storage: array of points
    if (coords.length > 0 && Array.isArray(coords[0]) && !Array.isArray(coords[0][0])) {
      return coords.map(cleanPoint);
    }
    // For MultiPolygon: array of polygons, each with array of rings
    if (
      coords.length > 0 &&
      Array.isArray(coords[0]) &&
      Array.isArray(coords[0][0]) &&
      Array.isArray(coords[0][0][0]) &&
      typeof coords[0][0][0][0] === "number"
    ) {
      return coords.map((polygon) =>
        polygon.map((ring) => (Array.isArray(ring) ? ring.map(cleanPoint) : ring)),
      );
    }
    // For GeoJSON Polygon: array of rings (array of points)
    if (coords.length > 0 && Array.isArray(coords[0]) && Array.isArray(coords[0][0])) {
      return coords.map((ring) => (Array.isArray(ring) ? ring.map(cleanPoint) : ring));
    }
    return coords;
  };

  const isClosedCoords = (coords) => {
    if (!coords || coords.length < 2) return false;
    const first = coords[0];
    const last = coords[coords.length - 1];
    const tolerance = 1e-10;
    if (Array.isArray(first) && Array.isArray(last) && first.length === last.length) {
      return first.every((val, idx) => Math.abs(val - last[idx]) < tolerance);
    }
    return false;
  };

  // Import convention: we do not require/store closed polygon rings.
  // If the input is closed (last == first), drop the last duplicate vertex.
  const stripClosingVertex = (coords) => {
    if (!coords || coords.length < 2) return coords;
    if (isClosedCoords(coords)) return coords.slice(0, -1);
    return coords;
  };

  const shouldConvertToPolygon = (feature, coords) => {
    const closedByFlag = feature.properties?.closed === true;
    const closedByCoords = isClosedCoords(coords);
    return (closedByFlag || closedByCoords) && coords.length >= 4;
  };

  const normalizeFieldName = (name) =>
    String(name || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const geometryField = view.spec.description.keys.find((key) => {
    const normalizedName = normalizeFieldName(key.name);
    const normalizedTitle = normalizeFieldName(key.title || "");
    return (
      normalizedName === "geometrie" ||
      normalizedName === "geometry" ||
      normalizedTitle === "geometrie" ||
      normalizedTitle === "geometry"
    );
  });

  const getFieldTitle = (keyname) => {
    let title;
    if (isDXFFile) {
      title = view.spec.description.keys.find((k) => k.name === keyname)?.title || keyname;
    } else {
      title = fieldMatching[keyname]?.field_title || keyname;
    }
    return normalizeAccents(title);
  };

  for (let feature_id = 0; feature_id < geojson.features.length; feature_id++) {
    const feature = geojson.features[feature_id];

    // DXF: skip ignored layers (title block / frame)
    if (isDXFFile) {
      const layerName = feature?.properties?.layer;
      if (layerName && layerMapping?.[layerName]?.ignored) {
        skippedCount += 1;
        skippedReasons["ignored_layer"] = (skippedReasons["ignored_layer"] || 0) + 1;
        continue;
      }
    }

    if (!feature?.geometry?.type) {
      skippedCount++;
      const reason = "Missing or invalid geometry";
      skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
      add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
      continue;
    }

    if (
      !feature.geometry.coordinates ||
      (Array.isArray(feature.geometry.coordinates) && feature.geometry.coordinates.length === 0)
    ) {
      skippedCount++;
      const reason = "Empty coordinates";
      skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
      add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
      continue;
    }

    const annotation = {};
    annotation.geometry = JSON.parse(JSON.stringify(feature.geometry));

    // Geometry normalization (clean coordinates only after flattening to the internal format)
    if (annotation.geometry.type === "MultiPolygon") {
      const c = annotation.geometry.coordinates;
      // Rule: MultiPolygon must contain exactly 1 polygon with exactly 1 ring. Otherwise we skip with an error.
      if (!Array.isArray(c) || c.length !== 1 || !Array.isArray(c[0]) || c[0].length !== 1) {
        skippedCount++;
        const reason = "Invalid MultiPolygon (expected exactly 1 polygon with exactly 1 ring)";
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
        add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
        continue;
      }
      annotation.geometryName = "polygon";
      annotation.geometry.type = "Polygon";
      // Internal convention: Polygon coordinates are an array of points ([ [x,y], ... ]), not rings-of-rings.
      annotation.geometry.coordinates = stripClosingVertex(c[0][0]);
    } else if (annotation.geometry.type === "Point") {
      annotation.geometryName = "point";
    } else if (annotation.geometry.type === "LineString") {
      const coords = annotation.geometry.coordinates;
      if (!coords || coords.length === 0) {
        skippedCount++;
        const reason = "LineString with no coordinates";
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
        add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
        continue;
      }
      if (coords.length === 1) {
        annotation.geometry.coordinates = [coords[0], coords[0]];
        add_error(
          "CAUTION! LineString with only 1 point - duplicated to create valid geometry (length will be 0)",
          feature_id,
        );
      }
      if (shouldConvertToPolygon(feature, coords)) {
        annotation.geometryName = "polygon";
        annotation.geometry.type = "Polygon";
        // If we convert to polygon, keep coordinates as-is (do not enforce closure).
        annotation.geometry.coordinates = stripClosingVertex([...coords]);
      } else {
        annotation.geometryName = "line";
      }
    } else if (annotation.geometry.type === "MultiLineString") {
      const c = annotation.geometry.coordinates;
      // Rule: MultiLineString must contain exactly 1 segment. Otherwise we skip with an error.
      if (!Array.isArray(c) || c.length !== 1) {
        skippedCount++;
        const reason = "Invalid MultiLineString (expected exactly 1 segment)";
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
        add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
        continue;
      }
      const firstSegment = c[0];
      if (!firstSegment || firstSegment.length === 0) {
        skippedCount++;
        const reason = "LineString with no coordinates";
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
        add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
        continue;
      }
      annotation.geometryName = "line";
      annotation.geometry.type = "LineString";
      annotation.geometry.coordinates = firstSegment;
      if (firstSegment.length === 1) {
        annotation.geometry.coordinates = [firstSegment[0], firstSegment[0]];
        add_error(
          "CAUTION! LineString with only 1 point - duplicated to create valid geometry (length will be 0)",
          feature_id,
        );
      }
    } else if (annotation.geometry.type === "Polygon") {
      annotation.geometryName = "polygon";
      if (
        annotation.geometry.coordinates &&
        Array.isArray(annotation.geometry.coordinates) &&
        annotation.geometry.coordinates.length > 0 &&
        Array.isArray(annotation.geometry.coordinates[0]) &&
        Array.isArray(annotation.geometry.coordinates[0][0])
      ) {
        annotation.geometry.coordinates = annotation.geometry.coordinates[0];
      }
      annotation.geometry.coordinates = stripClosingVertex(annotation.geometry.coordinates);
    } else if (annotation.geometry.type === "Circle" || annotation.geometry.type === "circle") {
      const coords = annotation.geometry.coordinates;
      if (!coords) {
        skippedCount++;
        const reason = "Circle geometry with no coordinates";
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
        add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
        continue;
      }
      const points = Array.isArray(coords[0]) && typeof coords[0][0] === "number" ? coords : [coords];
      let sumX = 0;
      let sumY = 0;
      let count = 0;
      for (const p of points) {
        if (Array.isArray(p) && p.length >= 2 && typeof p[0] === "number" && typeof p[1] === "number") {
          sumX += p[0];
          sumY += p[1];
          count += 1;
        }
      }
      if (count === 0) {
        skippedCount++;
        const reason = "Circle geometry with invalid coordinates";
        skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
        add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
        continue;
      }
      annotation.geometryName = "point";
      annotation.geometry.type = "Point";
      annotation.geometry.coordinates = [sumX / count, sumY / count];
    } else {
      skippedCount++;
      const reason = `Unknown geometry type: ${annotation.geometry.type}`;
      skippedReasons[reason] = (skippedReasons[reason] || 0) + 1;
      add_error(`CAUTION! ${reason} (annotation will be ignored)`, feature_id);
      continue;
    }

    if (annotation.geometry?.coordinates != null) {
      annotation.geometry.coordinates = cleanCoordinates(annotation.geometry.coordinates);
    }

    // Prepare data from properties via fieldMatching
    const data = {};
    for (const [keyName, matching] of Object.entries(fieldMatching || {})) {
      const v = feature?.properties?.[matching.matched_json_field];
      data[keyName] = typeof v === "string" ? normalizeAccents(v) : v;
    }

    const results = cleanCorruptedAnnotation(view.spec.description.keys, { data }, "invert_with_default");

    // Map geometry type to geometry field option
    if (geometryField && geometryField.type === "select" && Array.isArray(geometryField.options)) {
      let geometryValue = null;
      if (annotation.geometryName === "line") geometryValue = "Linéaire";
      else if (annotation.geometryName === "point") geometryValue = "Ponctuel";
      else if (annotation.geometryName === "polygon") geometryValue = "Surfacique";

      if (geometryValue) {
        const matchingOption = geometryField.options.find(
          (opt) => opt.text && normalizeFieldName(opt.text) === normalizeFieldName(geometryValue),
        );
        if (matchingOption) results.data[geometryField.name] = matchingOption.id;
      }
    }

    annotation.data = results.data;
    annotation.corrupted_field_names = results.corrupted_field_names;
    annotation.original_properties = JSON.parse(JSON.stringify(feature.properties || {}));

    // DXF diagnostic info passthrough
    if (isDXFFile && feature.properties) {
      if (feature.properties.parsingIssues) annotation.parsingIssues = feature.properties.parsingIssues;
      if (feature.properties.diagnosticInfo) {
        try {
          annotation.diagnosticInfo = JSON.parse(feature.properties.diagnosticInfo);
        } catch {
          annotation.diagnosticInfo = feature.properties.diagnosticInfo;
        }
      }
    }

    annotations.push(annotation);

    if (results.corrupted_field_names.length > 0) {
      const key_error1 =
        "Missing fields (default values will be used): " +
        results.corrupted_field_names.filter((keyname) => data[keyname] == null).map(getFieldTitle).join(", ");
      add_error(key_error1, feature_id);
      const key_error2 =
        "Invalid fields (will be replaced by default values): " +
        results.corrupted_field_names.filter((keyname) => data[keyname] != null).map(getFieldTitle).join(", ");
      add_error(key_error2, feature_id);
    }

    if (isDXFFile && annotation.parsingIssues) {
      add_error(`DXF Parsing Issue (feature ${feature_id}): ${annotation.parsingIssues}`, feature_id);
    }
  }

  if (skippedCount > 0) {
    let summary = `⚠️ ${skippedCount} feature(s) skipped: `;
    summary += Object.entries(skippedReasons)
      .map(([reason, count]) => `${reason} (${count})`)
      .join(", ");
    errors_types["SKIPPED_FEATURES"] = [summary];
  }

  const warnings = [];
  for (const [errortype, nbs] of Object.entries(errors_types)) {
    if (Array.isArray(nbs) && nbs.length > 0) {
      if (errortype === "SKIPPED_FEATURES") warnings.push(nbs[0]);
      else warnings.push(`${errortype} in ${nbs.length} annotation(s).`);
    }
  }

  return { annotations, warnings };
}

